import axios from "axios";

// ADMIN_PANEL_ARCHITECTURE_TEMPLATE.md §5, followed exactly.
//
// `withCredentials: true` is REQUIRED, not optional: the backend authenticates via a JWT in an
// httpOnly cookie named `__session` (technical-foundation §6.2, frozen). The frontend never holds,
// reads or attaches a token, which makes XSS token theft structurally impossible.
// Part 3 BD-7: the one cookie holds a staff and a customer session side by side; this header tells the API that
// the admin panel speaks for the STAFF one, so a customer signed in to the portal in this browser is never read
// here. A plain link (an export) cannot send a header and carries `?vvr_aud=staff` instead (see api/reports.js).
export const SESSION_AUDIENCE = "staff";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  headers: { "X-VVR-Audience": SESSION_AUDIENCE },
});

// ── AN EMPTY FILTER IS NOT A FILTER ─────────────────────────────────────────
// Every list screen keeps its filters in the query string and reads them back with
// `params.get("customerId") || ""`, so an untouched filter arrives here as "". Axios serialises that
// into `?customerId=`, and the backend guards read `if (filters.customerId !== undefined)` — a
// present-but-empty value passes the guard and is then rejected by `assertValidId`, so an UNFILTERED
// list answered 422 "Invalid customerId".
//
// That killed the Bookings and Payments boards outright: both rendered "Not available — Invalid
// customerId" with a zero count, for every user, on first load. No service-level test could see it,
// because a test passes a filter OBJECT, and only a browser turns "" into a query parameter.
//
// Stripping empties here rather than in each page fixes the whole class at once and matches what the
// backend means by "not supplied". `false` and `0` are kept — those are real values; only null,
// undefined and the empty string are dropped.
api.interceptors.request.use((config) => {
  if (config.params && typeof config.params === "object") {
    config.params = Object.fromEntries(
      Object.entries(config.params).filter(([, v]) => v !== "" && v !== null && v !== undefined)
    );
  }
  return config;
});

// Global 401 handling. Any expired or revoked session anywhere in the app lands the user on /login
// with an explanatory reason — no page needs to handle it.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const path = window.location.pathname;
      // Don't bounce off the auth pages themselves: a failed login is a legitimate 401 and must
      // render its error inline, not redirect.
      if (path !== "/login") {
        const code = error.response?.data?.code;
        const reason = code === "SESSION_REPLACED" ? "replaced" : "expired";

        // ── PART 4 §4.7 · WHERE THE USER WAS, CARRIED ACROSS THE RE-LOGIN ─────────────────────
        // `ProtectedRoute` already preserves the destination via `<Navigate state={{ from }}>`, but
        // that only covers arriving at a guarded route with no session. A session that expires
        // MID-USE lands here instead, and this is a full document load — React Router state does not
        // survive it — so the destination was lost and every re-login returned to the dashboard.
        //
        // THE PATH AND ITS QUERY ONLY. `location.search` carries the operational state Part 4 §4.4
        // put there (view, filters, page); it is the same information a bookmark would hold. Nothing
        // is added that was not already in the address bar.
        //
        // IT RESTORES A DESTINATION, NEVER AN ACTION. This is a GET navigation target. The request
        // that just failed is NOT retried and no form is resubmitted — §4.7 is explicit that a
        // mutation must be performed again, deliberately, by the user.
        const intended = `${path}${window.location.search}`;
        const q = new URLSearchParams({ session: reason });
        // "/" is the default landing anyway, so carrying it adds nothing but a longer URL.
        if (intended !== "/") q.set("next", intended);
        window.location.href = `/login?${q}`;
      }
    }
    // Always re-throw. The interceptor owns the GLOBAL consequence of a 401; the calling code still
    // owns the local one (stop the spinner, keep the form filled). Swallowing leaves buttons spinning.
    return Promise.reject(error);
  }
);

export default api;
